/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Notify from "@eisinc/App/Components/Notify/Notify";
import Spinner from "@eisinc/App/Components/Spinner/Spinner";
import UploadFilesModal from "@eisinc/App/Components/UploadFilesModal/UploadFilesModal";
import checkQuoteEmail from "@eisinc/App/Handlers/checkQuoteEmail";
import { validateRecaptcha } from "@eisinc/App/Services/RecaptchaService";
import { RequestQuote } from "@eisinc/App/Services/RequestQuoteService";
import { linkStyle02WithAnchor } from "@eisinc/App/SharedStyles/LinksStyles";
import { checkPostalCode, doScroll } from "@eisinc/App/Utilities/helpers";
import Button from "@eisinc/MobiusOverrides/Button";
import CheckboxGroup from "@eisinc/MobiusOverrides/CheckboxGroup/CheckboxGroup";
import Select from "@eisinc/MobiusOverrides/Select";
import TextArea from "@eisinc/MobiusOverrides/TextArea/TextArea";
import TextField from "@eisinc/MobiusOverrides/TextField";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { screen } from "@eisinc/XDL/Helpers";
import Info from "@eisinc/XDL/Icons/Info";
import { validateEmail } from "@eisinc/XDL/Utilities/inputValidation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrencies,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, CurrencyModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox/Checkbox";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import { GridContainer, GridItem, Hidden, Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* eslint-disable */
import Reaptcha from 'reaptcha';
/* eslint-enable */

interface RequestQuoteFormProps extends WidgetProps { }

const mapDispatchToProps = {
    loadCurrentCountries,
    checkQuoteEmail,
};

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    currencies: getCurrencies(state),
    customSettings: getSettingsCollection(state).customSettings,
    homePageLink: getPageLinkByPageType(state, "HomePage")?.url || "/",
    isAuthenticated: getSession(state).isAuthenticated,
});

type Props = RequestQuoteFormProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface RequestQuoteFormWidgetStyles {
    requestQuoteFormWrapper?: InjectableCss;
    leftContentGridItem?: GridItemProps;
    rightContentGridItem?: GridItemProps;
    formControlWrapperGridItem?: GridItemProps;
    formControlWrapperFullGridItem?: GridItemProps;
    sectionTitle?: TypographyProps;
    spacer?: InjectableCss;
    emailAlreadyExists?: InjectableCss;
    filesWrapper?: InjectableCss;
}

export const requestQuoteFormWidgetStyles: RequestQuoteFormWidgetStyles = {
    requestQuoteFormWrapper: {
        css: css`
            position: relative;
        `,
    },
    leftContentGridItem: {
        width: [12, 12, 12, 12, 8],
        css: css`
            flex-direction: column;
        `,
    },
    rightContentGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            ${screen("lte", "lg")`display: none;`};
        `,
    },
    formControlWrapperGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            position: relative;
        `,
    },
    sectionTitle: {
        variant: "h4",
        as: "h2",
        css: css`
            margin-bottom: 10px;
        `,
    },
    spacer: {
        css: css`
            height: 30px;
        `,
    },
    formControlWrapperFullGridItem: {
        width: 12,
    },
    emailAlreadyExists: {
        css: css`
            margin-bottom: 15px;
            max-width: 100%;

            ${linkStyle02WithAnchor};
        `,
    },
    filesWrapper: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const styles = requestQuoteFormWidgetStyles;

const RequestQuoteFormWidget: FC<Props> = ({
    countries,
    currencies,
    loadCurrentCountries,
    customSettings,
    checkQuoteEmail,
    homePageLink,
    history,
    isAuthenticated,
}) => {
    let error: any = false;

    const initialValidateEmailExistence = { emailAlreadyExists: false, isActive: false };
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [attention, setAttention] = useState("");
    const [country, setCountry] = useState<CountryModel | undefined>(undefined);
    const [currency, setCurrency] = useState<CurrencyModel | undefined>(undefined);
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [states, setStates] = useState<StateModel[]>([]);
    const [state, setState] = useState<StateModel | undefined>(undefined);
    const [postalCode, setPostalCode] = useState("");
    const [addFiles, setAddFiles] = useState(false);
    const [notes, setNotes] = useState("");
    const [subscribeToMailChimp, setSubscribeToMailChimp] = useState(false);
    const [captchaElm, setCaptchaElm] = useState<any>({} as any);
    const [validateEmailExistence, setValidateEmailExistence] = useState(initialValidateEmailExistence);
    const [firstNameError, setFirstNameError] = useState(error);
    const [lastNameError, setLastNameError] = useState(error);
    const [emailError, setEmailError] = useState(error);
    const [phoneError, setPhoneError] = useState(error);
    const [companyNameError, setCompanyNameError] = useState(error);
    const [countryError, setCountryError] = useState(error);
    const [currencyError, setCurrencyError] = useState(error);
    const [address1Error, setAddress1Error] = useState(error);
    const [cityError, setCityError] = useState(error);
    const [stateError, setStateError] = useState(error);
    const [postalCodeError, setPostalCodeError] = useState(error);
    const [openDocumentsModal, setOpenDocumentsModal] = useState(false);
    const [returnedFiles, setReturnedFiles] = useState([]);
    const [userVerified, setUserVerified] = useState(false);

    const uploadFilesModalRef: any = useRef();

    const toasterContext: any = useContext(ToasterContext);

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    useEffect(() => {
        setStates(country?.states || []);

        if (country) {
            const selectedCurrency = currencies?.find(c => c.currencyCode === country?.properties.defaultCurrency);

            setCurrency(selectedCurrency);

            return;
        }

        setCurrency(undefined);
    }, [country]);

    useEffect(() => {
        const errorMessage = validateEmail(email);

        if (!errorMessage) {
            checkIfEmailAlreadyExists(email);
        }
    }, [email]);

    useEffect(() => {
        if (userVerified) {
            continueHandlingFormSubmit();
        }
    }, [userVerified]);

    const onRecaptchaVerified = () => {
        setUserVerified(true);
    };

    const forwardRef = useCallback(node => {
        setCaptchaElm(node as any);
    }, []);

    const validateRequiredFields = (fieldName: string, fieldValue: any) => {
        if (!fieldValue || (typeof fieldValue === "string" && !fieldValue.trim())) {
            let errorMessage;

            switch (fieldName) {
                case "firstName":
                    errorMessage = siteMessage("CreateNewAccountInfo_FirstName_Required");
                    setFirstNameError(errorMessage);

                    break;
                case "lastName":
                    errorMessage = siteMessage("CreateNewAccountInfo_LastName_Required");
                    setLastNameError(errorMessage);

                    break;
                case "email":
                    errorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
                    setEmailError(errorMessage);

                    break;
                case "phone":
                    errorMessage = siteMessage("CreateNewAccountInfo_Phone_Required");
                    setPhoneError(errorMessage);

                    break;
                case "companyName":
                    errorMessage = siteMessage("CreateNewAccountInfo_CompanyName_Required");
                    setCompanyNameError(errorMessage);

                    break;
                case "country":
                    errorMessage = siteMessage("CreateNewAccountInfo_Country_Required");
                    setCountryError(errorMessage);

                    break;
                case "currency":
                    errorMessage = siteMessage("CreateNewAccountInfo_Currency_Required");
                    setCurrencyError(errorMessage);

                    break;
                case "address1":
                    errorMessage = siteMessage("CreateNewAccountInfo_Address1_Required");
                    setAddress1Error(errorMessage);

                    break;
                case "city":
                    errorMessage = siteMessage("CreateNewAccountInfo_City_Required");
                    setCityError(errorMessage);

                    break;
                case "state":
                    errorMessage = siteMessage("CreateNewAccountInfo_State_Required");
                    setStateError(errorMessage);

                    break;
                case "postalCode":
                    errorMessage = siteMessage("CreateNewAccountInfo_PostalCode_Required");
                    setPostalCodeError(errorMessage);

                    break;
            }

            error = true;
        }
    };

    const validateEmailAddress = (email: string) => {
        const errorMessage = validateEmail(email);

        setEmailError(errorMessage);

        if (errorMessage) {
            error = true;
        }
    };

    const validatePostalCode = (postalCode: string) => {
        if (country?.abbreviation) {
            if (checkPostalCode(country.abbreviation, postalCode)) {
                setPostalCodeError(translate("Invalid Postal Code."));
                error = true;
            }
        }
    };

    const checkIfEmailAlreadyExists = (email: string) => {
        if (!emailError) {
            setIsSubmitting(true);

            const params = {
                email,
            };

            checkQuoteEmail({
                ...params,
                onSuccess: (result: any) => {
                    const emailAlreadyExists = result?.emailAlreadyExists;
                    const isActive = result?.isActive;

                    const resultObj = {
                        emailAlreadyExists,
                        isActive,
                    };

                    setValidateEmailExistence(resultObj);
                    setIsSubmitting(false);
                },
                onError: error => {
                    setIsSubmitting(false);

                    if (error) {
                        setValidateEmailExistence({ emailAlreadyExists: false, isActive: false });
                    }
                },
            });
        }
    };

    const handleChange = (fieldName: string, fieldValue: any) => {
        switch (fieldName) {
            case "firstName":
                setFirstNameError("");
                setFirstName(fieldValue);

                break;
            case "lastName":
                setLastNameError("");
                setLastName(fieldValue);

                break;
            case "email":
                setEmail(fieldValue);

                if (isSubmitted) {
                    validateEmailAddress(fieldValue);
                }

                break;
            case "phone":
                setPhoneError("");
                setPhone(fieldValue);

                break;
            case "companyName":
                setCompanyNameError("");
                setCompanyName(fieldValue);

                break;
            case "country":
                setCountryError("");
                setCountry(countries?.find(country => country.id === fieldValue));

                break;
            case "currency":
                setCurrencyError("");
                setCurrency(currencies?.find(currency => currency.id === fieldValue));

                break;
            case "address1":
                setAddress1Error("");
                setAddress1(fieldValue);

                break;
            case "city":
                setCityError("");
                setCity(fieldValue);

                break;
            case "state":
                setStateError("");
                setState(states?.find(state => state.id === fieldValue));

                break;
            case "postalCode":
                setPostalCodeError("");
                setPostalCode(fieldValue);

                if (isSubmitted) {
                    validatePostalCode(fieldValue);
                }
                break;
        }
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();

        if (!userVerified) {
            captchaElm.execute();

            return;
        }

        continueHandlingFormSubmit();
    };

    const continueHandlingFormSubmit = () => {
        setIsSubmitted(true);

        if (!isAuthenticated) {
            validateRequiredFields("firstName", firstName);
            validateRequiredFields("lastName", lastName);
            validateRequiredFields("email", email);
            validateRequiredFields("phone", phone);

            if (email) {
                validateEmailAddress(email);
            }
        }

        validateRequiredFields("companyName", companyName);
        validateRequiredFields("country", country);
        validateRequiredFields("currency", currency);
        validateRequiredFields("address1", address1);
        validateRequiredFields("city", city);
        validateRequiredFields("state", state);
        validateRequiredFields("postalCode", postalCode);

        if (postalCode) {
            validatePostalCode(postalCode);
        }

        if (error || (validateEmailExistence?.emailAlreadyExists && validateEmailExistence?.isActive)) {
            doScroll();

            return;
        }

        setIsSubmitting(true);

        captchaElm.getResponse().then((response: any) => {
            validateRecaptcha(response).then(
                () => {
                    const formData = new FormData();

                    if (!isAuthenticated) {
                        formData.append(`email`, email);
                        formData.append(`firstName`, firstName);
                        formData.append(`lastName`, lastName);
                        formData.append(`phone`, phone);
                    }

                    formData.append(`companyName`, companyName);
                    formData.append(`attention`, attention);
                    formData.append(`country`, country?.id || "");
                    formData.append(`currency`, currency?.id || "");
                    formData.append(`address1`, address1);
                    formData.append(`address2`, address2);
                    formData.append(`city`, city);
                    formData.append(`state`, state?.id || "");
                    formData.append(`postalCode`, postalCode);
                    formData.append(`notes`, notes);
                    formData.append(`subscribeToMailChimp`, subscribeToMailChimp ? "true" : "false");

                    for (let i = 0; i < returnedFiles.length; i++) {
                        const file = returnedFiles[i] as any;

                        formData.append(`file[${i}]`, file, file.name || "");
                    }

                    RequestQuote(formData).then(
                        () => {
                            toasterContext.addToast({
                                body: siteMessage("Custom_RequestQuote_SuccessMessage"),
                                messageType: "success",
                            });
                            setIsSubmitting(false);
                            history.push(homePageLink);
                        },
                        () => {
                            setIsSubmitting(false);
                            toasterContext.addToast({
                                body: translate("An error occurred, please try again later."),
                                messageType: "danger",
                            });
                        },
                    );
                },
                () => {
                    captchaElm.execute();
                },
            );
        });
    };

    return (
        <StyledWrapper {...styles.requestQuoteFormWrapper}>
            <form onSubmit={handleSubmit} noValidate>
                <GridContainer>
                    <GridItem {...styles.leftContentGridItem}>
                        {!isAuthenticated && (
                            <>
                                <Typography {...styles.sectionTitle}>{translate("Contact Info")}</Typography>
                                {validateEmailExistence?.emailAlreadyExists && validateEmailExistence?.isActive && (
                                    <>
                                        <Hidden aria-invalid={true} />
                                        <Notify
                                            {...styles.emailAlreadyExists}
                                            messageType="danger"
                                            body={siteMessage("Request_Quote_Form_Email_Already_Exists_Active")}
                                        />
                                    </>
                                )}
                                <GridContainer>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            label={translate("First Name")}
                                            onChange={e => handleChange("firstName", e.currentTarget.value)}
                                            value={firstName}
                                            data-test-selector="requestQuoteForm_firstName"
                                            error={isSubmitted && firstNameError}
                                            maxLength={25}
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            label={translate("Last Name")}
                                            onChange={e => handleChange("lastName", e.currentTarget.value)}
                                            value={lastName}
                                            data-test-selector="requestQuoteForm_lastName"
                                            error={isSubmitted && lastNameError}
                                            maxLength={25}
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            type="email"
                                            label={translate("Email Address")}
                                            onChange={e => handleChange("email", e.currentTarget.value)}
                                            value={email}
                                            data-test-selector="requestQuoteForm_email"
                                            error={isSubmitted && emailError}
                                            maxLength={256}
                                            minLength={5}
                                            required
                                        />
                                    </GridItem>
                                    <GridItem {...styles.formControlWrapperGridItem}>
                                        <TextField
                                            type="tel"
                                            label={translate("Phone")}
                                            onChange={e => handleChange("phone", e.currentTarget.value)}
                                            value={phone}
                                            data-test-selector="requestQuoteForm_phone"
                                            error={isSubmitted && phoneError}
                                            placeholder="(###) ###-####"
                                            required
                                        />
                                    </GridItem>
                                </GridContainer>
                                <StyledWrapper {...styles.spacer} />
                                <Typography {...styles.sectionTitle}>{translate("Company Info")}</Typography>
                            </>
                        )}
                        <GridContainer>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    label={translate("Company Name")}
                                    onChange={e => handleChange("companyName", e.currentTarget.value)}
                                    value={companyName}
                                    data-test-selector="requestQuoteForm_companyName"
                                    error={isSubmitted && companyNameError}
                                    maxLength={100}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    label={translate("Attention")}
                                    onChange={e => setAttention(e.currentTarget.value)}
                                    value={attention}
                                    data-test-selector="requestQuoteForm_companyName"
                                    maxLength={100}
                                    optional={true}
                                />
                            </GridItem>
                            {countries && countries?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        name="country"
                                        label={translate("Country")}
                                        value={country?.id || ""}
                                        onChange={e => handleChange("country", e.target.value)}
                                        error={isSubmitted && countryError}
                                        data-test-selector="requestQuoteForm_country"
                                        required
                                    >
                                        <option value="">{translate("Select Country")}</option>
                                        {countries.map(country => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </Select>
                                    <StyledWrapper
                                        css={css`
                                            position: absolute;
                                            right: 15px;
                                            top: 12px;
                                        `}
                                    >

                                        <Tooltip
                                            text={siteMessage("Custom_RequestQuoteForm_Other_Country_Hint") as string}
                                        />
                                    </StyledWrapper>
                                </GridItem>
                            )}
                            {currencies && currencies?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        label={translate("Currency")}
                                        value={currency?.id || ""}
                                        onChange={e => handleChange("currency", e.target.value)}
                                        error={isSubmitted && currencyError}
                                        data-test-selector="requestQuoteForm_currency"
                                        required
                                    >
                                        <option value="">{translate("Select Currency")}</option>
                                        {currencies.map(currency => (
                                            <option value={currency.id} key={currency.id}>
                                                {currency.currencyCode}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    label={translate("Address 1")}
                                    name="address1"
                                    value={address1}
                                    maxLength={100}
                                    onChange={e => handleChange("address1", e.currentTarget.value)}
                                    error={isSubmitted && address1Error}
                                    data-test-selector="requestQuoteForm_address1"
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    name="address2"
                                    label={translate("Address 2")}
                                    value={address2}
                                    maxLength={100}
                                    onChange={e => setAddress2(e.currentTarget.value)}
                                    data-test-selector="requestQuoteForm_address1"
                                    optional={true}
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    name="city"
                                    label={translate("Town/City")}
                                    onChange={e => handleChange("city", e.currentTarget.value)}
                                    value={city}
                                    data-test-selector="requestQuoteForm_city"
                                    error={isSubmitted && cityError}
                                    maxLength={100}
                                    required
                                />
                            </GridItem>
                            {countries && countries?.length && (
                                <GridItem {...styles.formControlWrapperGridItem}>
                                    <Select
                                        name="state"
                                        label={translate("State")}
                                        value={state?.id || ""}
                                        disabled={!states.length}
                                        onChange={e => handleChange("state", e.target.value)}
                                        error={isSubmitted && stateError}
                                        data-test-selector="requestQuoteForm_state"
                                        required
                                    >
                                        <option value="">{translate("Select State")}</option>
                                        {states.map(state => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </Select>
                                </GridItem>
                            )}
                            <GridItem {...styles.formControlWrapperGridItem}>
                                <TextField
                                    name="postalCode"
                                    label={translate("Postal Code")}
                                    onChange={e => handleChange("postalCode", e.currentTarget.value)}
                                    disabled={!country}
                                    value={postalCode}
                                    data-test-selector="requestQuoteForm_postalCode"
                                    error={isSubmitted && postalCodeError}
                                    maxLength={50}
                                    required
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <TextArea
                                    name="notes"
                                    label={translate("Tell us what you are looking for")}
                                    onChange={e => setNotes(e.currentTarget.value)}
                                    value={notes}
                                    data-test-selector="requestQuoteForm_postalCode"
                                    maxLength={300}
                                    optional={true}
                                />
                            </GridItem>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <CheckboxGroup>
                                    <Checkbox
                                        onChange={(e, value) => {
                                            setAddFiles(value);
                                            setOpenDocumentsModal(value);

                                            if (!value) {
                                                uploadFilesModalRef.current.resetFields();
                                            }
                                        }}
                                        checked={addFiles}
                                        data-test-selector="requestQuoteForm_addFiles"
                                    >
                                        {siteMessage("Custom_CreateAccount_UploadFilesMessage")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                            <StyledWrapper {...styles.filesWrapper}>
                                <UploadFilesModal
                                    ref={uploadFilesModalRef}
                                    openModal={openDocumentsModal}
                                    headline={translate("Upload Files")}
                                    handleClose={() => {
                                        setOpenDocumentsModal(false);
                                        setAddFiles(false);
                                    }}
                                    handleContinue={files => {
                                        setOpenDocumentsModal(false);
                                        setReturnedFiles(files);

                                        if (!files.length) {
                                            setAddFiles(false);
                                        }
                                    }}
                                />
                            </StyledWrapper>
                            <GridItem {...styles.formControlWrapperFullGridItem}>
                                <CheckboxGroup>
                                    <Checkbox
                                        onChange={(e, value) => setSubscribeToMailChimp(value)}
                                        checked={subscribeToMailChimp}
                                        data-test-selector="requestQuoteForm_subscription"
                                    >
                                        {siteMessage("SignIn_Sign_Up_Newsletter")}
                                    </Checkbox>
                                </CheckboxGroup>
                            </GridItem>
                        </GridContainer>
                        <StyledWrapper {...styles.spacer} />
                        <Button type="submit" disabled={isSubmitting}>
                            {translate("Submit")}
                        </Button>
                    </GridItem>
                    <GridItem {...styles.rightContentGridItem} />
                </GridContainer>
                <Reaptcha
                    ref={forwardRef}
                    sitekey={(customSettings as any).reCaptchaSitekey}
                    size="invisible"
                    onVerify={onRecaptchaVerified}
                    onExpire={() => {
                        setUserVerified(false);
                    }}
                />
                {isSubmitting && <Spinner />}
            </form>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RequestQuoteFormWidget)),
    definition,
};

export default widgetModule;
